import React from "react";
import { Link } from "react-router-dom";
import { AlertOctagon, Clock } from "lucide-react";
import { Panel, StatusBadge } from "@/components/kmt/ui";
import { useLoad, useOwnerApi } from "@/lib/api";
import { SERVICE_LABEL, STAGES, ct, prettyPhone, usd } from "@/lib/format";
import { PageTitle } from "./DashLayout";

function Rows({ rows, empty, money, testid }: { rows: any[]; empty: string; money?: boolean; testid: string }) {
  if (!rows.length) return <p className="text-[15px] text-mist/60" data-testid={`${testid}-empty`}>{empty}</p>;
  return (
    <ul className="divide-y divide-gold/15" data-testid={testid}>
      {rows.map((r) => (
        <li key={r.id}>
          <Link to={`/dashboard/bookings/${r.id}`} className="flex min-h-[56px] flex-wrap items-center justify-between gap-3 py-3 transition-colors duration-200 hover:text-gold" data-testid={`${testid}-row-${r.ref}`}>
            <span><strong className="font-medium">{r.name}</strong> · {prettyPhone(r.phone)}<br /><span className="text-[14px] text-mist/65">{SERVICE_LABEL[r.service]} · {ct(r.pickup_at)}</span></span>
            {money ? <span className="text-[15px]">{usd(r.remaining)} due {ct(r.balance_due_at)}</span> : <StatusBadge status={r.status} label={r.status_label} />}
          </Link>
        </li>
      ))}
    </ul>
  );
}

export default function Overview() {
  const api = useOwnerApi();
  const { data, error } = useLoad(() => api.get("/dashboard").then((r) => r.data), [api]);
  if (error) return <p className="text-red-200">{error}</p>;
  if (!data) return <p className="text-mist/70">Loading…</p>;
  return (
    <div className="flex flex-col gap-6">
      <PageTitle title="Overview" sub={`${data.active_total} active bookings`} />
      {data.overdue.length > 0 && (
        <div role="alert" className="flex items-center gap-3 rounded-md border border-red-300/50 bg-red-500/15 p-4 text-[15px] text-red-100" data-testid="overdue-alert">
          <AlertOctagon /> {data.overdue.length} booking{data.overdue.length > 1 ? "s have" : " has"} an overdue balance. Rides never cancel automatically.
        </div>
      )}
      <div className="grid grid-cols-2 gap-px overflow-hidden rounded-md border border-gold/20 bg-gold/20 md:grid-cols-4 xl:grid-cols-6">
        {STAGES.slice(0, 6).map((s) => (
          <Link key={s.value} to={`/dashboard/bookings?status=${s.value}`} className="bg-aubergine/80 p-4 transition-colors duration-200 hover:bg-plum/40" data-testid={`count-${s.value}`}>
            <p className="font-display text-3xl">{data.counts[s.value] || 0}</p>
            <p className="text-[14px] text-mist/70">{s.label}</p>
          </Link>
        ))}
      </div>
      <div className="grid gap-6 xl:grid-cols-2">
        <Panel title="Inbox: new requests" data-testid="inbox-panel" className="xl:row-span-2">
          <Rows rows={data.inbox} empty="No new requests." testid="inbox" />
        </Panel>
        <Panel title="Overdue balances" data-testid="overdue-panel">
          <Rows rows={data.overdue} empty="Nothing overdue." money testid="overdue" />
        </Panel>
        <Panel title="Balances due in the next 24 hours" data-testid="due-soon-panel" actions={<Clock className="text-gold" size={20} />}>
          <Rows rows={data.due_next_24h} empty="No balances due in the next 24 hours." money testid="due-soon" />
        </Panel>
      </div>
    </div>
  );
}
